/**
 * ProgressPage - Comprehensive athlete progress dashboard
 *
 * Shows:
 * - Current week and month performance grades
 * - Historical progress with trends
 * - Drill completion stats
 * - Coach feedback summary
 * - Streak tracking
 */

import { useState, useEffect } from 'react';
import {
  useUser,
  getAthleteProgress,
  type Organization,
  type AthleteProgress,
  type ProgressSnapshot,
  GRADE_CONFIG,
} from '@tss/shared';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Target,
  Flame,
  Star,
  AlertCircle,
  BarChart3,
  Award,
  Clock,
  Video,
} from 'lucide-react';
import { Badge, EmptyState } from '@tss/shared/ui';
import { PageHeader, Skeleton } from '../components/UI';

interface ProgressPageProps {
  org: Organization | null;
  proxyAthleteId?: string;
}

export function ProgressPage({ org, proxyAthleteId }: ProgressPageProps) {
  const { userId } = useUser();
  const [progress, setProgress] = useState<AthleteProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use proxy athlete ID if in proxy mode
  const effectiveUserId = proxyAthleteId || userId;

  useEffect(() => {
    loadProgress();
  }, [org, effectiveUserId]);

  async function loadProgress() {
    if (!org || !effectiveUserId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getAthleteProgress(org.org_id, effectiveUserId);
      setProgress(data);
    } catch (err: any) {
      console.error('Failed to load progress:', err);
      setError(err.message || 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Skeleton height={36} width="50%" style={{ marginBottom: 8 }} />
        <Skeleton height={20} width="60%" style={{ marginBottom: 32 }} />
        <Skeleton.Card count={1} />
        <div style={{ marginTop: 24 }}>
          <Skeleton.Stats count={4} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <PageHeader
          title="My Progress"
          description="Track your training journey"
          icon={BarChart3}
          iconColor="var(--purple-600)"
        />
        <div className="card">
          <EmptyState
            icon={<AlertCircle />}
            title="Couldn't load progress"
            description={error}
            action={
              <button className="btn btn-primary" onClick={loadProgress}>
                Try Again
              </button>
            }
          />
        </div>
      </div>
    );
  }

  if (!progress) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <PageHeader
          title="My Progress"
          description="Track your training journey and performance over time"
          icon={BarChart3}
          iconColor="var(--purple-600)"
        />
        <div className="card">
          <EmptyState
            icon={<BarChart3 />}
            title="No progress data yet"
            description="Start completing drills and uploading videos to see your progress here!"
          />
        </div>
      </div>
    );
  }

  // Default empty period for null-safety
  const defaultPeriod = {
    drills_completed: 0,
    drills_assigned: 0,
    completion_rate: 0,
    completion_percent: 0,
    grade: 'DEVELOPING' as const,
    grade_label: 'Developing',
    grade_color: '#F59E0B',
    grade_emoji: '📈',
    grade_description: 'Room for improvement. Stay consistent!',
    videos_uploaded: 0,
    streak_days: 0,
    active_days: 0,
    excellent_flags: 0,
    needs_work_flags: 0,
  };

  // Use defaults if monthly/weekly is missing
  const monthly = progress.monthly || defaultPeriod;
  const weekly = progress.weekly || defaultPeriod;

  const monthlyGrade = monthly.grade || 'DEVELOPING';
  const weeklyGrade = weekly.grade || 'DEVELOPING';
  const monthlyGradeInfo = GRADE_CONFIG[monthlyGrade] || GRADE_CONFIG.DEVELOPING;
  const weeklyGradeInfo = GRADE_CONFIG[weeklyGrade] || GRADE_CONFIG.DEVELOPING;

  // Calculate trend from history
  const getTrend = () => {
    if (!progress.history || progress.history.length < 2) return null;
    const recent = progress.history[0];
    const previous = progress.history[1];
    const diff = recent.completion_rate - previous.completion_rate;
    if (diff > 0.05) return 'up';
    if (diff < -0.05) return 'down';
    return 'same';
  };

  const trend = getTrend();

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <PageHeader
        title="My Progress"
        description="Track your training journey and performance over time"
        icon={BarChart3}
        iconColor="var(--purple-600)"
      />

      {/* Current streak — featured callout (SKOOL-33) */}
      {monthly?.streak_days != null && monthly.streak_days > 0 && (
        <div className="page-featured" style={{ marginBottom: 24 }}>
          <div className="page-featured-strip">CURRENT STREAK</div>
          <div className="page-featured-body">
            <h3 className="page-featured-title">
              {monthly.streak_days} day{monthly.streak_days === 1 ? '' : 's'}
            </h3>
            <p className="page-featured-subtitle">
              Keep showing up. Most athletes plateau here — you're past it.
            </p>
          </div>
        </div>
      )}

      {/* Current Month — section-wrapped (SKOOL-33) */}
      <section className="page-section">
        <header className="page-section-header">
          <p className="page-section-label">THIS MONTH</p>
          <h2 className="page-section-title">Monthly performance</h2>
          <div className="page-section-rule" aria-hidden="true" />
        </header>
        <div
          className="card animate-fade-in"
          style={{
            background: `linear-gradient(135deg, ${monthlyGradeInfo.color}15 0%, ${monthlyGradeInfo.color}05 100%)`,
            border: `2px solid ${monthlyGradeInfo.color}30`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
            <Calendar size={16} style={{ color: 'var(--gray-500)' }} />
            <span
              style={{
                fontSize: '12px',
                fontWeight: '600',
                color: 'var(--gray-500)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              This Month
            </span>
            {trend && (
              <span
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color:
                    trend === 'up' ? '#10B981' : trend === 'down' ? '#EF4444' : 'var(--gray-500)',
                }}
              >
                {trend === 'up' && <TrendingUp size={14} />}
                {trend === 'down' && <TrendingDown size={14} />}
                {trend === 'same' && <Minus size={14} />}
                {trend === 'up' ? 'Improving' : trend === 'down' ? 'Declining' : 'Steady'}
              </span>
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div
                style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: `${monthlyGradeInfo.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '36px',
                }}
              >
                {monthlyGradeInfo.emoji}
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '32px',
                    fontWeight: '800',
                    color: monthlyGradeInfo.color,
                    margin: 0,
                  }}
                >
                  {monthlyGradeInfo.label}
                </h2>
                <p
                  style={{
                    fontSize: '14px',
                    color: 'var(--gray-600)',
                    marginTop: '4px',
                    maxWidth: '300px',
                  }}
                >
                  {monthlyGradeInfo.description}
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p
                style={{
                  fontSize: '48px',
                  fontWeight: '700',
                  color: monthlyGradeInfo.color,
                  lineHeight: 1,
                }}
              >
                {monthly.completion_percent}%
              </p>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '4px' }}>
                Completion Rate
              </p>
            </div>
          </div>

          {/* Progress bar with grade markers */}
          <div style={{ marginTop: '24px' }}>
            <div
              style={{
                height: '12px',
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '6px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(monthly.completion_percent, 100)}%`,
                  background: `linear-gradient(90deg, ${monthlyGradeInfo.color}80, ${monthlyGradeInfo.color})`,
                  borderRadius: '6px',
                  transition: 'width 0.5s ease',
                }}
              />
              {/* Grade threshold markers */}
              {[30, 50, 70, 90].map((pct) => (
                <div
                  key={pct}
                  style={{
                    position: 'absolute',
                    left: `${pct}%`,
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    background: 'rgba(255,255,255,0.5)',
                  }}
                />
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '8px',
                fontSize: '11px',
                color: 'var(--gray-400)',
              }}
            >
              <span>0%</span>
              <span style={{ position: 'relative', left: '-10%' }}>Poor</span>
              <span style={{ position: 'relative', left: '-5%' }}>Developing</span>
              <span>Good</span>
              <span style={{ position: 'relative', left: '5%' }}>Great</span>
              <span>Elite</span>
            </div>
          </div>

          {/* Stats Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
              gap: '16px',
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: `1px solid ${monthlyGradeInfo.color}20`,
            }}
          >
            <StatItem
              icon={Target}
              label="Drills Done"
              value={`${monthly.drills_completed}/${monthly.drills_assigned}`}
              color={monthlyGradeInfo.color}
            />
            <StatItem
              icon={Clock}
              label="Active Days"
              value={monthly.active_days.toString()}
              color="#3B82F6"
            />
            <StatItem
              icon={Video}
              label="Videos"
              value={monthly.videos_uploaded.toString()}
              color="#10B981"
            />
            <StatItem
              icon={Flame}
              label="Streak"
              value={`${monthly.streak_days} days`}
              color="#F59E0B"
            />
            {(monthly.excellent_flags || 0) > 0 && (
              <StatItem
                icon={Star}
                label="Coach Stars"
                value={(monthly.excellent_flags || 0).toString()}
                color="var(--purple-500)"
              />
            )}
          </div>
        </div>
      </section>

      {/* Weekly Stats — section-wrapped (SKOOL-33) */}
      <section className="page-section">
        <header className="page-section-header">
          <p className="page-section-label">THIS WEEK</p>
          <h2 className="page-section-title">Weekly performance</h2>
          <div className="page-section-rule" aria-hidden="true" />
        </header>
        <div className="card animate-fade-in" style={{ animationDelay: '100ms' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: `${weeklyGradeInfo.color}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
              }}
            >
              {weeklyGradeInfo.emoji}
            </div>
            <div>
              <h3
                style={{ fontSize: '16px', fontWeight: '600', color: 'var(--gray-900)', margin: 0 }}
              >
                This Week
              </h3>
              <p style={{ fontSize: '12px', color: weeklyGradeInfo.color, fontWeight: '600' }}>
                {weeklyGradeInfo.label} • {weekly.completion_percent}%
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            <div>
              <p style={{ fontSize: '24px', fontWeight: '700', color: 'var(--gray-900)' }}>
                {weekly.drills_completed}
                <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--gray-500)' }}>
                  /{weekly.drills_assigned}
                </span>
              </p>
              <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Drills Completed</p>
            </div>
            <div>
              <p style={{ fontSize: '24px', fontWeight: '700', color: 'var(--gray-900)' }}>
                {weekly.active_days}
              </p>
              <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Active Days</p>
            </div>
          </div>

          {/* Weekly progress bar */}
          <div style={{ marginTop: '16px' }}>
            <div
              style={{
                height: '8px',
                background: 'var(--gray-100)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(weekly.completion_percent, 100)}%`,
                  background: weeklyGradeInfo.color,
                  borderRadius: '4px',
                  transition: 'width 0.5s ease',
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Coach Feedback — section-wrapped (SKOOL-33) */}
      {((monthly.excellent_flags || 0) > 0 || (monthly.needs_work_flags || 0) > 0) && (
        <section className="page-section">
          <header className="page-section-header">
            <p className="page-section-label">FEEDBACK</p>
            <h2 className="page-section-title">From your coaches</h2>
            <div className="page-section-rule" aria-hidden="true" />
          </header>
          <div className="card animate-fade-in" style={{ animationDelay: '200ms' }}>
            <h3
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--gray-900)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Award size={20} style={{ color: 'var(--purple-500)' }} />
              Coach Feedback This Month
            </h3>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {(monthly.excellent_flags || 0) > 0 && (
                <div
                  style={{
                    flex: '1 1 150px',
                    padding: '16px',
                    background: 'rgba(var(--purple-rgb), 0.1)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid rgba(var(--purple-rgb), 0.2)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <Star size={20} style={{ color: 'var(--purple-500)' }} />
                    <span
                      style={{ fontSize: '24px', fontWeight: '700', color: 'var(--purple-500)' }}
                    >
                      {monthly.excellent_flags || 0}
                    </span>
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--gray-600)' }}>
                    Drills marked as excellent by your coach
                  </p>
                </div>
              )}
              {(monthly.needs_work_flags || 0) > 0 && (
                <div
                  style={{
                    flex: '1 1 150px',
                    padding: '16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '8px',
                    }}
                  >
                    <AlertCircle size={20} style={{ color: '#EF4444' }} />
                    <span style={{ fontSize: '24px', fontWeight: '700', color: '#EF4444' }}>
                      {monthly.needs_work_flags || 0}
                    </span>
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--gray-600)' }}>
                    Drills that need more practice
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Progress History — section-wrapped (SKOOL-33) */}
      {progress.history && progress.history.length > 0 && (
        <section className="page-section">
          <header className="page-section-header">
            <p className="page-section-label">TRENDS</p>
            <h2 className="page-section-title">Progress over time</h2>
            <div className="page-section-rule" aria-hidden="true" />
          </header>
          <div className="card animate-fade-in" style={{ animationDelay: '300ms' }}>
            <h3
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'var(--gray-900)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <BarChart3 size={20} style={{ color: 'var(--purple-600)' }} />
              Progress History
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {progress.history.map((snapshot, index) => (
                <HistoryItem key={`${snapshot.period_start}-${index}`} snapshot={snapshot} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function StatItem({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Target;
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 8px',
        }}
      >
        <Icon size={18} style={{ color }} />
      </div>
      <p
        style={{
          fontSize: '18px',
          fontWeight: '700',
          color: 'var(--gray-900)',
          marginBottom: '2px',
        }}
      >
        {value}
      </p>
      <p style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{label}</p>
    </div>
  );
}

function HistoryItem({ snapshot }: { snapshot: ProgressSnapshot }) {
  const gradeInfo = GRADE_CONFIG[snapshot.grade] || GRADE_CONFIG.DEVELOPING;
  const periodLabel = snapshot.period_type === 'MONTHLY' ? 'Month' : 'Week';

  // Format date range
  const startDate = new Date(snapshot.period_start);
  const endDate = new Date(snapshot.period_end);
  const dateRange =
    snapshot.period_type === 'MONTHLY'
      ? startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

  const completionPercent = Math.round(snapshot.completion_rate * 100);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        background: 'var(--gray-50)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: `${gradeInfo.color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          flexShrink: 0,
        }}
      >
        {gradeInfo.emoji}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '14px' }}>
            {gradeInfo.label}
          </span>
          {snapshot.period_type === 'MONTHLY' ? (
            <Badge tone="pro">{periodLabel}</Badge>
          ) : (
            <span
              style={{
                fontSize: '11px',
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'rgba(59, 130, 246, 0.1)',
                color: '#3B82F6',
                fontWeight: '600',
              }}
            >
              {periodLabel}
            </span>
          )}
        </div>
        <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>{dateRange}</p>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <p style={{ fontSize: '16px', fontWeight: '700', color: gradeInfo.color }}>
          {completionPercent}%
        </p>
        <p style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
          {snapshot.drills_completed}/{snapshot.drills_assigned}
        </p>
      </div>
    </div>
  );
}

export default ProgressPage;
